import React, { useState, useEffect } from "react";
import { BarChart3, TrendingUp, Users, Target, Download, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#b3b3b3", "#8b5cf6", "#06b6d4", "#10b981", "#ef4444", "#eab308"];

export const AdminAnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("tika_admin_token");
      const res = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des analytics");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!data) return;
    const rows = [["Date", "Commandes", "Revenus (EUR)", "Billets"]];
    data.sales_timeline.forEach(d => {
      rows.push([d.date, d.orders, d.revenue.toFixed(2), d.tickets]);
    });
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tika_analytics_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV terminé");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span className="text-muted-foreground">Chargement des analytics...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Aucune donnée disponible
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const parts = dateStr.split("-");
    return `${parts[2]}/${parts[1]}`;
  };

  return (
    <div className="space-y-6" data-testid="admin-analytics-tab">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="glass-card rounded-lg p-3 text-center">
          <TrendingUp className="w-5 h-5 mx-auto mb-1 text-gray-400" />
          <div className="text-xl font-bold">{data.total_revenue?.toFixed(0) || 0}€</div>
          <div className="text-xs text-muted-foreground">Revenus totaux</div>
        </div>
        <div className="glass-card rounded-lg p-3 text-center">
          <BarChart3 className="w-5 h-5 mx-auto mb-1 text-violet-500" />
          <div className="text-xl font-bold">{data.total_orders || 0}</div>
          <div className="text-xs text-muted-foreground">Commandes</div>
        </div>
        <div className="glass-card rounded-lg p-3 text-center">
          <Users className="w-5 h-5 mx-auto mb-1 text-cyan-500" />
          <div className="text-xl font-bold">{data.total_tickets_sold || 0}</div>
          <div className="text-xs text-muted-foreground">Billets vendus</div>
        </div>
        <div className="glass-card rounded-lg p-3 text-center">
          <Target className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
          <div className="text-xl font-bold">{data.scan_rate || 0}%</div>
          <div className="text-xs text-muted-foreground">Taux de scan</div>
        </div>
      </div>

      {/* Sales Timeline */}
      <div className="glass-card rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Ventes plateforme (30 jours)</h3>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="admin-export-csv-btn">
            <Download className="w-3 h-3 mr-1" />
            Export CSV
          </Button>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data.sales_timeline.map(d => ({ ...d, dateLabel: formatDate(d.date) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 10 }} stroke="rgba(255,255,255,0.5)" />
            <YAxis tick={{ fontSize: 10 }} stroke="rgba(255,255,255,0.5)" />
            <Tooltip
              contentStyle={{ background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
              labelStyle={{ color: "#fff" }}
            />
            <Line type="monotone" dataKey="revenue" stroke="#b3b3b3" strokeWidth={2} dot={false} name="Revenus (€)" />
            <Line type="monotone" dataKey="tickets" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Billets" />
            <Line type="monotone" dataKey="orders" stroke="#06b6d4" strokeWidth={2} dot={false} name="Commandes" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Tarif Distribution */}
        <div className="glass-card rounded-lg p-4">
          <h3 className="font-semibold text-sm mb-3">Répartition par tarif</h3>
          {data.tarif_distribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={data.tarif_distribution}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={65}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {data.tarif_distribution.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Aucune donnée</p>
          )}
        </div>

        {/* Top Events */}
        <div className="glass-card rounded-lg p-4">
          <h3 className="font-semibold text-sm mb-3">Top événements (revenus)</h3>
          {data.top_events.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.top_events.map(e => ({ ...e, shortTitle: e.title.slice(0, 12) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="shortTitle" tick={{ fontSize: 9 }} stroke="rgba(255,255,255,0.5)" />
                <YAxis tick={{ fontSize: 10 }} stroke="rgba(255,255,255,0.5)" />
                <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Bar dataKey="revenue" fill="#b3b3b3" radius={[4, 4, 0, 0]} name="Revenus (€)" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Aucune donnée</p>
          )}
        </div>
      </div>

      {/* Fill Rates */}
      {data.fill_rates.length > 0 && (
        <div className="glass-card rounded-lg p-4">
          <h3 className="font-semibold text-sm mb-3">Taux de remplissage par événement</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {data.fill_rates.map((event) => (
              <div key={event.event_id} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-28 truncate">{event.title}</span>
                <div className="flex-1 h-3 bg-muted/20 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.min(event.fill_rate, 100)}%`,
                      background: event.fill_rate >= 80 ? "#ef4444" : event.fill_rate >= 50 ? "#b3b3b3" : "#10b981"
                    }}
                  />
                </div>
                <span className="text-xs font-medium w-20 text-right">
                  {event.sold}/{event.capacity} ({event.fill_rate}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
